// @file Vuex Store module for Snackbars displayed by GlobalSnackbar.vue
import window from '@@/bits/global'
import { __ } from '@@/bits/intl'
import { SnackbarNotificationType } from '@@/enums'
import type { DispatchableAction } from '@@/types'
import { uniqueId } from 'lodash-es'
import type { Module } from 'vuex'

const DEFAULT_TIMEOUT = 3000

interface GlobalSnackbarNotificationVuex {
  notificationType?: SnackbarNotificationType
  uid: string
  message: string
  actionText?: string
  actionTextActions?: DispatchableAction[]
  timeout?: number
  persist?: boolean
  iconName?: string
  originalIndex: number // used to calculate snackbar position when a snackbar is removed
}

interface GlobalSnackbarState {
  snackbarArray: GlobalSnackbarNotificationVuex[]
}

const GlobalSnackbarsModule: Module<GlobalSnackbarState, object> = {
  namespaced: true,
  state: {
    snackbarArray: [],
  },
  getters: {
    snackbarArray(state): GlobalSnackbarNotificationVuex[] {
      return state.snackbarArray
    },
  },
  mutations: {
    NEW_SNACKBAR(state, newSnackbar: GlobalSnackbarNotificationVuex): void {
      state.snackbarArray = [...state.snackbarArray, newSnackbar]
    },
    REMOVE_SNACKBAR(state, targetSnackbarUID: string): void {
      state.snackbarArray = state.snackbarArray.filter((snackbar) => snackbar.uid !== targetSnackbarUID)
    },
  },
  actions: {
    setSnackbar({ dispatch, commit, state }, newSnackbar: Partial<GlobalSnackbarNotificationVuex>): void {
      const uid = uniqueId('snack_')

      // prevent overlap of snackbars on page by calculating unique index for each snackbar
      const originalIndex =
        state.snackbarArray.length === 0
          ? 0
          : Math.max(...state.snackbarArray.map((snackbar) => snackbar.originalIndex)) + 1

      if (newSnackbar.notificationType) {
        // if notification type exists use preset based on notification type
        const snackbarDataPreset: Record<
          SnackbarNotificationType,
          { [P in keyof GlobalSnackbarNotificationVuex]?: GlobalSnackbarNotificationVuex[P] }
        > = {
          [SnackbarNotificationType.error]: {
            iconName: 'exclamation',
          },
          [SnackbarNotificationType.success]: {
            iconName: 'checkmark',
          },
        }

        commit('NEW_SNACKBAR', {
          ...newSnackbar,
          uid,
          originalIndex,
          ...snackbarDataPreset[newSnackbar.notificationType],
        })
      } else {
        commit('NEW_SNACKBAR', { ...newSnackbar, uid, originalIndex })
      }

      // if timeout is set or if persist is falsey
      // set snackbar to expire automatically
      if (newSnackbar.timeout || !newSnackbar.persist) {
        window.setTimeout(() => {
          dispatch('removeSnackbar', uid)
        }, newSnackbar.timeout ?? DEFAULT_TIMEOUT)
      }
    },
    removeSnackbar({ commit }, targetSnackbarUID: string): void {
      commit('REMOVE_SNACKBAR', targetSnackbarUID)
    },
    executeActions({ dispatch }, actions: DispatchableAction[] = []): void {
      actions.forEach((actionHash): void => {
        dispatch(actionHash.type, actionHash.payload, { root: true })
      })
    },
    genericFetchError({ dispatch }): void {
      void dispatch('setSnackbar', {
        message: __('Oops! Something went wrong. Please try again.'),
        notificationType: SnackbarNotificationType.error,
      })
    },
  },
}

export default GlobalSnackbarsModule
export { SnackbarNotificationType }
export type { GlobalSnackbarNotificationVuex, GlobalSnackbarState }
